import React from "react";

// FIBA half-court: portrait, hoop at top. 15m wide × 14m long.
const S = 30; // 30 px per meter
const BACKBOARD_WIDTH = 1.8 * S;
const BACKBOARD_FROM_BASELINE = 1.2 * S;
const BASKETBALL_RIM_R = (0.45 / 2) * S;
export const SVG_W = 15 * S; // 450px wide
export const SVG_H = 14 * S; // 420px height
const BX = SVG_W / 2;
const BY = 1.575 * S;
const KEY_LEN = 5.8 * S;
const KEY_HALF = 2.45 * S;
const FT_R = 1.8 * S;
const PT3_R = 6.75 * S;
const PT3_SIDE = 0.9 * S;
const RA_R = 1.25 * S;
const PT3_X_L = PT3_SIDE;
const PT3_X_R = SVG_W - PT3_SIDE;
const PT3_Y = BY + Math.sqrt(PT3_R ** 2 - (BX - PT3_X_L) ** 2);
const NZ_MARK_W = 0.1 * S;
const NZ_TICK_YS = [1.75, 3.85, 4.7].map((m) => m * S);
const NZ_BLOCK_Y = 2.6 * S;
const NZ_BLOCK_H = 0.4 * S;

const CHART_BG = "#FFFFFF";
const PAINT_BG = "#FFFFFF";
export const LINE = "#3C414A";
const LW = 1.5;

export const MAKE_COLOR = "#1F5F63";
export const MISS_COLOR = "#D92D20";
export const SHOT_OPACITY = 0.75;
export const MAKE_STROKE = 2.3;
export const MISS_STROKE = 1.8;
export const MARKER_R = 5.5;

// Note: the chart is rotated 90° vs Scorekeeper (baseline/hoop displayed on top),
// so the court's horizontal axis maps to GL y and the vertical axis maps to GL x.
// convert GL coordinates (0–100) → SVG px
export const toSvgX = (glY) => (1 - glY / 100) * SVG_W;
export const toSvgY = (glX) => (1 - glX / 100) * SVG_H;

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
// inverse: SVG px → GL coordinates (0–100, rounded ints)
export const svgToGlY = (px) => clamp(Math.round((1 - px / SVG_W) * 100), 0, 100);
export const svgToGlX = (py) => clamp(Math.round((1 - py / SVG_H) * 100), 0, 100);

export function CourtMarkings() {
  return (
    <>
      {/* Background */}
      <rect width={SVG_W} height={SVG_H} fill={CHART_BG} />
      {/* Paint area */}
      <rect x={BX - KEY_HALF} y={0} width={KEY_HALF * 2} height={KEY_LEN} fill={PAINT_BG} />
      {/* Court boundary */}
      <rect x={0} y={0} width={SVG_W} height={SVG_H} fill="none" stroke={LINE} strokeWidth={LW} />
      {/* Key outline */}
      <rect x={BX - KEY_HALF} y={0} width={KEY_HALF * 2} height={KEY_LEN} fill="none" stroke={LINE} strokeWidth={LW} />
      {/* Neutral zone marks */}
      {NZ_TICK_YS.map((y, i) => (
        <React.Fragment key={i}>
          <line x1={BX - KEY_HALF - NZ_MARK_W} y1={y} x2={BX - KEY_HALF} y2={y} stroke={LINE} strokeWidth={LW} />
          <line x1={BX + KEY_HALF} y1={y} x2={BX + KEY_HALF + NZ_MARK_W} y2={y} stroke={LINE} strokeWidth={LW} />
        </React.Fragment>
      ))}
      <rect x={BX - KEY_HALF - NZ_MARK_W} y={NZ_BLOCK_Y} width={NZ_MARK_W} height={NZ_BLOCK_H} fill={LINE} />
      <rect x={BX + KEY_HALF} y={NZ_BLOCK_Y} width={NZ_MARK_W} height={NZ_BLOCK_H} fill={LINE} />
      {/* Free throw semicircle */}
      <path
        d={`M ${BX - FT_R},${KEY_LEN} A ${FT_R},${FT_R} 0 1,0 ${BX + FT_R},${KEY_LEN}`}
        fill="none"
        stroke={LINE}
        strokeWidth={LW}
      />
      {/* 3pt line */}
      <path
        d={`M ${PT3_X_L},0 L ${PT3_X_L},${PT3_Y} A ${PT3_R},${PT3_R} 0 0,0 ${PT3_X_R},${PT3_Y} L ${PT3_X_R},0`}
        fill="none"
        stroke={LINE}
        strokeWidth={LW}
      />
      {/* Restricted area */}
      <path
        d={`M ${BX - RA_R},${BACKBOARD_FROM_BASELINE} L ${BX - RA_R},${BY} A ${RA_R},${RA_R} 0 0,0 ${BX + RA_R},${BY} L ${BX + RA_R},${BACKBOARD_FROM_BASELINE}`}
        fill="none"
        stroke={LINE}
        strokeWidth={LW}
      />
      {/* Line from rim to backboard */}
      <line x1={BX} y1={BY - BASKETBALL_RIM_R} x2={BX} y2={BACKBOARD_FROM_BASELINE} stroke={LINE} strokeWidth={LW} />
      {/* Backboard */}
      <line
        x1={BX - BACKBOARD_WIDTH / 2}
        y1={BACKBOARD_FROM_BASELINE}
        x2={BX + BACKBOARD_WIDTH / 2}
        y2={BACKBOARD_FROM_BASELINE}
        stroke={LINE}
        strokeWidth={2}
      />
      {/* Basket */}
      <circle cx={BX} cy={BY} r={BASKETBALL_RIM_R} fill="none" stroke={LINE} strokeWidth={2} />
    </>
  );
}

// Presentational shot glyph: make = ring, miss = cross. Caller positions it.
export function ShotMarker({ cx, cy, made }) {
  if (made) {
    return <circle cx={cx} cy={cy} r={MARKER_R} fill="none" stroke={MAKE_COLOR} strokeWidth={MAKE_STROKE} />;
  }
  const d = MARKER_R;
  return (
    <>
      <line
        x1={cx - d}
        y1={cy - d}
        x2={cx + d}
        y2={cy + d}
        stroke={MISS_COLOR}
        strokeWidth={MISS_STROKE}
        strokeLinecap="round"
      />
      <line
        x1={cx - d}
        y1={cy + d}
        x2={cx + d}
        y2={cy - d}
        stroke={MISS_COLOR}
        strokeWidth={MISS_STROKE}
        strokeLinecap="round"
      />
    </>
  );
}
